import { toast } from 'react-toastify';
import {
  AccountingDocType,
  AccountingDocumentEntity,
} from '../../../../api/types/accounting/accounting-documents-entity';
import { CardBrandType } from '../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';
import { formatDate } from '../../../../utils/date-format';

const docTypeLabel = (type: AccountingDocType): string => {
  switch (type) {
    case AccountingDocType.RECEIPT:
      return 'קבלה';
    case AccountingDocType.RECEIPT_REFUND:
      return 'קבלת - החזר';
    case AccountingDocType.BILL:
      return 'חשבון';
    case AccountingDocType.INVOICE:
      return 'חשבונית';
    case AccountingDocType.DONATION_RECEIPT:
      return 'קבלה לתרומה';
    case AccountingDocType.CASH_WITHDRAWAL:
      return 'שובר משיכה מקופה';
    default:
      return type;
  }
};

const documentSourceLabel = (row: AccountingDocumentEntity): string => {
  if (row.userPaymentId !== null) return 'סטודנט';
  if (row.crmPaymentId !== null || row.crmBillId !== null) return 'CRM';
  return 'חסר';
};

const documentBranchesLabel = (row: AccountingDocumentEntity): string => {
  if (!row.branches || row.branches.length === 0) return '';
  return row.branches
    .map((branch) => branch.branchName?.trim())
    .filter((name): name is string => Boolean(name))
    .join(', ');
};

const cardBrandLabel = (brand: CardBrandType): string => {
  switch (brand) {
    case CardBrandType.VISA:
      return 'Visa';
    case CardBrandType.MASTERCARD:
      return 'Mastercard';
    case CardBrandType.ISRACARD:
      return 'Isracard';
    case CardBrandType.AMERICAN_EXPRESS:
      return 'American Express';
    case CardBrandType.DINERS_CLUB:
      return 'Diners Club';
    case CardBrandType.DISCOVER:
      return 'Discover';
    case CardBrandType.JCB:
      return 'JCB';
    case CardBrandType.MAESTRO:
      return 'Maestro';
    case CardBrandType.PRIVATE_LABEL:
      return 'כרטיס פרטי';
    default:
      return brand;
  }
};

const creditCardCellContent = (row: AccountingDocumentEntity): string => {
  const brand = row.creditCardBrand ?? null;
  const last4 = row.creditCardLast4?.trim() || null;
  if (!brand && !last4) return '';
  const brandText = brand ? cardBrandLabel(brand) : '';
  const last4Text = last4 ? `${last4}` : '';
  return [brandText, last4Text].filter(Boolean).join(' ');
};

const formatAmount = (amount: number | null | undefined): string => {
  if (typeof amount !== 'number') return '';
  const abs = Math.abs(amount).toFixed(2);
  return amount < 0 ? `${abs}-` : abs;
};

const HEADERS = [
  'מספר מסמך',
  'סוג מסמך',
  'מוען',
  'סכום',
  'כרטיס אשראי',
  'מקור',
  'שלוחות',
  'מסמך אב',
  'תאריך יצירה',
  'נוצר על ידי',
] as const;

/**
 * Exports the currently selected accounting documents as a single Excel sheet.
 * Columns mirror the accounting-documents table (excluding the file-link column).
 */
export const exportAccountingDocumentsToExcelOperation = (
  selectedDocuments: AccountingDocumentEntity[],
  setExporting: (exporting: boolean) => void,
) => {
  try {
    if (!selectedDocuments || selectedDocuments.length === 0) {
      toast.warn('יש לבחור לפחות מסמך אחד לייצוא');
      return;
    }

    setExporting(true);

    const dataRows = selectedDocuments.map((row) => [
      row.docNumber ?? '',
      docTypeLabel(row.docType),
      row.subject?.trim() || '',
      formatAmount(row.amount),
      creditCardCellContent(row),
      documentSourceLabel(row),
      documentBranchesLabel(row),
      row.parentDocNumber ?? '',
      row.createdAt ? formatDate(row.createdAt as Date) : '',
      row.createdByName?.trim() || '',
    ]);

    const today = new Date();
    const datePart = `${String(today.getDate()).padStart(2, '0')}-${String(
      today.getMonth() + 1,
    ).padStart(2, '0')}-${today.getFullYear()}`;

    generateAndDownloadExcelFile([[...HEADERS], ...dataRows], {
      fileName: `מסמכים חשבונאיים (${selectedDocuments.length}) - ${datePart}`,
      sheetName: 'מסמכים חשבונאיים',
    });

    toast.success('המסמכים יוצאו לאקסל בהצלחה');
    setExporting(false);
  } catch (e) {
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
    setExporting(false);
  }
};
